import React, { useState, useEffect } from "react";
import { useUser } from "../../providers/UserProvider";
import { PermissionEnum } from "../../../../shared/frontend/enums/PermissionEnum";
import { Stack, DefaultButton, Spinner, SpinnerSize } from "@fluentui/react";
import Download from "@mui/icons-material/Download";
import { useUseCaseContext } from "../../hooks/UseCaseContext";
import style from "./HeaderButtons.module.css";
import { useMetadataContext } from "../../providers/MetadataProvider";
import { DEFAULT_USE_CASE_NAME } from "../../utils/constants";
import DgEnvironmentSelector from "../DgEnvironmentSelector/DgEnvironmentSelector";
import { HISTORY_ICONS, LIBRARY_ICONS, IconButton } from "../IconButton";

interface HeaderButtonsProps {
  onSetShowHistoryPanel: () => void;
  onSetShowLibraryPanel: () => void;
  conversationId: string;
  onExport: (id: string, format: string) => Promise<void>;
  showExport: boolean;
  useCaseTitle: string;
  currentDg: string;
  onDgSwitch: (dg: string, displayName?: string) => void;
}

const HeaderButtons: React.FC<HeaderButtonsProps> = ({
  onSetShowHistoryPanel,
  onSetShowLibraryPanel,
  conversationId,
  onExport,
  showExport,
  useCaseTitle,
  currentDg,
  onDgSwitch,
}) => {
  const { useCase, setUseCase } = useUseCaseContext();
  const { hasPermission } = useUser();
  const { fetchingMetadata } = useMetadataContext();
  const canViewMultipleDg = hasPermission(PermissionEnum.VIEW_MULTIPLE_DG);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    setUseCase(useCase);
  }, [useCase, setUseCase]);

  const handleExportClick = async () => {
    if (isExporting || !conversationId) return;
    setIsExporting(true);
    try {
      await onExport(conversationId, "docx");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Stack horizontal className={style.ButtonsContainer}>
      <>
        {canViewMultipleDg && (
          <DgEnvironmentSelector
            currentDg={currentDg}
            onDgSwitch={onDgSwitch}
          />
        )}
        <IconButton
          label={
            fetchingMetadata ? "Loading virtual library..." : "Virtual library"
          }
          icons={LIBRARY_ICONS}
          disabled={fetchingMetadata || useCaseTitle !== DEFAULT_USE_CASE_NAME}
          onClick={onSetShowLibraryPanel}
        />
        <IconButton
          label="Chat history"
          icons={HISTORY_ICONS}
          onClick={onSetShowHistoryPanel}
        />

        {showExport && (
          <DefaultButton
            className={style.HeaderButton}
            onClick={handleExportClick}
            disabled={!conversationId || isExporting}
            onRenderIcon={
              isExporting
                ? () => <Spinner size={SpinnerSize.xSmall} />
                : () => <Download fontSize="small" />
            }
            text={isExporting ? "Exporting..." : "Export"}
          />
        )}
      </>
    </Stack>
  );
};

export default HeaderButtons;
